import {gql} from '@apollo/client';
import React, {useEffect} from 'react';

import {StarredItemsByObjectId, useStarredItemsQuery} from 'app-core/platform/starrable/CoreObjectStarIcon';
import {AlignmentSummary} from 'constants/AlignmentConstants';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {StorableWithPermissions} from 'constants/SampleConstants';
import * as FileUtil from 'folder-items/FileUtil';
import normalizeFolder from 'folders/util/normalizeFolder';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ConnectionSidepanelItem from 'instruments/connections/connection-sidepanel/ConnectionSidepanelItem';
import {
  StorableValidationInfoById,
  useStorableValidationInfoQuery,
} from 'inventory/location-validation/ParentStorableValidationUtils';
import {
  SidepanelItemListBioentitiesQuery,
  SidepanelItemListBioentitiesQueryVariables,
} from 'nav/sidepanel/listing/__generated/SidepanelItemListBioentitiesQuery';
import {
  SidepanelItemListFolderQuery,
  SidepanelItemListFolderQueryVariables,
} from 'nav/sidepanel/listing/__generated/SidepanelItemListFolderQuery';
import AlignmentSidepanelItem from 'nav/sidepanel/listing/AlignmentSidepanelItem';
import AnalysisSidepanelItem from 'nav/sidepanel/listing/AnalysisSidepanelItem';
import CollectibleSidepanelItem from 'nav/sidepanel/listing/CollectibleSidepanelItem';
import CollectionSidepanelItem from 'nav/sidepanel/listing/CollectionSidepanelItem';
import {SidepanelProcessItem} from 'processes/dashboard/ProcessManagementSidepanelConstants';
import ProcessManagementSidepanelItem from 'processes/dashboard/ProcessManagementSidepanelItem';
import RequestV2DefinitionSidepanelItem from 'processes/requests_v2/dashboard/RequestV2DefinitionSidepanelItem';
import RequestV2SubmissionSidepanelItem from 'processes/requests_v2/dashboard/RequestV2SubmissionSidepanelItem';
import {bioentityAliasInfosByBioentityId} from 'registry/graphql/BioentityAliasInfo';
import {Request} from 'requests/RequestConstants';
import RunSidepanelItem from 'runs/sidepanel/RunSidepanelItem';
import {SidepanelRun} from 'runs/sidepanel/RunsSidepanelConstants';
import TestDefinitionSidepanelItem from 'sample-tests/search/TestDefinitionSidepanelItem';
import TestOrderSidepanelItem from 'sample-tests/search/TestOrderSidepanelItem';
import {
  ContainerValidContentsInfoById,
  useContainerValidContentsQuery,
} from 'samples/containers/ValidContentsUtils';
import {
  AsyncFetcherLocation,
  AsyncFetcherIsFetchedState,
  ListableGroup,
  ListableItem,
} from 'search/SearchConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';
import FolderDropTarget, {getFolderDropError} from 'util/drag-and-drop/FolderDropTarget';

import {AnalysisSearchAttributesGraphQL} from '../../../../graphql-fragments/analysis-tool/__generated/AnalysisSearchAttributesGraphQL';
import {AnalyticsDashboardSearchAttributesGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsDashboardSearchAttributesGraphQL';
import {ConnectionListingGraphQL} from '../../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {CollectibleListingGraphQL} from '../../../../graphql-fragments/notebook/templates/__generated/CollectibleListingGraphQL';
import {TemplateCollectionListingGraphQL} from '../../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionListingGraphQL';
import {RequestV2DefinitionListingGraphQL} from '../../../../graphql-fragments/processes/requests-v2/__generated/RequestV2DefinitionListingGraphQL';
import {RequestV2SubmissionListingGraphQL} from '../../../../graphql-fragments/processes/requests-v2/__generated/RequestV2SubmissionListingGraphQL';
import {TestDefinitionListingGraphQL} from '../../../../graphql-fragments/sample_tests/__generated/TestDefinitionListingGraphQL';
import {TestOrderListingGraphQL} from '../../../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';
import AnalyticsDashboardSidepanelItem from './AnalyticsDashboardSidepanelItem';
import FolderFileSidepanelItem from './FolderFileSidepanelItem';
import GroupSidepanelItem from './GroupSidepanelItem';
import RequestSidepanelItem from './RequestSidepanelItem';
import StorableSidepanelItem from './StorableSidepanelItem';

interface Props {
  actionSource?: ActionOrigin;
  activeItemId: string | null;
  collection?: string | null;
  emptyMessage: string;
  finderId?: string | null;
  folder?: StringIDFolder | null;
  isCompact: boolean;
  isSearch: boolean;
  items: Array<ListableItem>;
  groups?: Array<ListableGroup>;
  asyncFetcherIsFetchedState: AsyncFetcherIsFetchedState;
  onUpdateAsyncFetcherIsFetchedState: (flagName: AsyncFetcherLocation, value: boolean) => void;
  openResult: (item: ListableItem) => void;
  /** Called when the user expands or collapses a group. */
  onToggleExpandedItems: (itemIds: Array<string>, isExpanded: boolean) => void;
  /** Called when the user requests to load more child items within a group. */
  onRequestMoreChildItems: (parentItemId: string) => void;
  /** The number of additional children that will be fetched when onRequestMoreChildItems is called. */
  childPageSize: number;
  section?: string | null;
  starredItemsByObjectId?: StarredItemsByObjectId;
}

interface EnhancedProps {
  bioentityAliasInfosByBioentityId: {[bioentityId: string]: Array<BioentityAliasInfo> | undefined};
  folder: StringIDFolder | null;
  containerValidContentsInfoById?: ContainerValidContentsInfoById;
  storableValidationInfoById?: StorableValidationInfoById;
}

export type SidepanelItemListProps = Props & EnhancedProps;

interface WrappedComponentProps {
  folderId?: string | null;
}

interface GroupChildrenPagerProps {
  onClick: () => void;
  numChildren: number;
}

// Only exported for tests
export function GroupChildrenPager({onClick, numChildren}: GroupChildrenPagerProps): JSX.Element {
  return (
    <div className="resultsPageLoader">
      <span className="sidepanelItem-childPadding">
        <a className="legacy-font-proxima-14" onClick={onClick}>
          Load more ({numChildren})
        </a>
      </span>
    </div>
  );
}

// Only exported for tests
export function GroupChildrenLoadingIndicator(): JSX.Element {
  return (
    <div className="sidepanel-childrenThrobberContainer">
      <CenteredHorizontalThrobber />
    </div>
  );
}

export class SidepanelItemList extends React.Component<SidepanelItemListProps & EnhancedProps> {
  _getFolderDropError = (): string | null =>
    this.props.folder == null ? null : getFolderDropError(this.props.folder);

  onToggleExpanded(group: ListableGroup): void {
    this.props.onToggleExpandedItems([group.id], !group.isExpanded);
  }

  renderSidepanelItem(item: ListableItem, isChild: boolean): JSX.Element {
    if (APIUtil.isStorable(item.id)) {
      return (
        <StorableSidepanelItem
          key={item.id}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          storable={item as StorableWithPermissions}
          openResult={this.props.openResult as (storable: StorableWithPermissions) => void}
          isChild={isChild}
          validContentsInfo={this.props.containerValidContentsInfoById?.[item.id]}
          validationStateInfo={this.props.storableValidationInfoById?.[item.id]}
        />
      );
    } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.REQUEST) {
      return (
        <RequestSidepanelItem
          key={item.id}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          request={item as Request}
          openResult={this.props.openResult}
          isChild={isChild}
        />
      );
    } else if (APIUtil.isAnalysis(item.id)) {
      return (
        <AnalysisSidepanelItem
          active={item.id === this.props.activeItemId}
          key={item.id}
          analysis={item as AnalysisSearchAttributesGraphQL}
          isCompact={this.props.isCompact}
          openResult={this.props.openResult as (item: AnalysisSearchAttributesGraphQL) => void}
          isChild={isChild}
        />
      );
    } else if (APIUtil.isAnalyticsDashboard(item.id)) {
      return (
        <AnalyticsDashboardSidepanelItem
          active={item.id === this.props.activeItemId}
          key={item.id}
          analyticsDashboard={item as AnalyticsDashboardSearchAttributesGraphQL}
          isCompact={this.props.isCompact}
          openResult={this.props.openResult as (item: AnalyticsDashboardSearchAttributesGraphQL) => void}
          isChild={isChild}
        />
      );
    } else if (
      APIUtil.isProcess(item.id) ||
      APIUtil.isProcessTask(item.id) ||
      APIUtil.isProcessOutput(item.id)
    ) {
      return (
        <ProcessManagementSidepanelItem
          key={item.id}
          processItem={item as SidepanelProcessItem}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          openResult={this.props.openResult}
          isChild={isChild}
        />
      );
    } else if (APIUtil.isConnection(item.id)) {
      return (
        <ConnectionSidepanelItem
          key={item.id}
          actionSource={this.props.actionSource}
          connection={item as ConnectionListingGraphQL}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          openResult={this.props.openResult}
          isChild={isChild}
        />
      );
    } else if (APIUtil.isRun(item.id)) {
      return (
        <RunSidepanelItem
          key={item.id}
          run={item as SidepanelRun}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          openResult={this.props.openResult}
          isChild={isChild}
        />
      );
    } else if (APIUtil.isSequenceAnalysis(item.id) || APIUtil.isProteinAlignment(item.id)) {
      return (
        <AlignmentSidepanelItem
          active={item.id === this.props.activeItemId}
          alignmentSummary={item as AlignmentSummary}
          isCompact={this.props.isCompact}
          key={item.id}
          isChild={isChild}
        />
      );
    } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.REQUEST_V2_DEFINITION) {
      // Request definitions are templates with a custom sidepanel view. This else/if block
      // should come before the generic template collectible block below.
      return (
        <RequestV2DefinitionSidepanelItem
          key={item.id}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          openResult={this.props.openResult}
          requestV2Definition={item as RequestV2DefinitionListingGraphQL}
          isChild={isChild}
        />
      );
    } else if (APIUtil.isTemplateCollectible(item.id)) {
      return (
        <CollectibleSidepanelItem
          key={item.id}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          collectible={item as CollectibleListingGraphQL}
          isChild={isChild}
          isStarred={this.props.starredItemsByObjectId?.[item.id] ?? false}
        />
      );
    } else if (APIUtil.isTemplateCollection(item.id)) {
      return (
        <CollectionSidepanelItem
          key={item.id}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          collection={item as TemplateCollectionListingGraphQL}
          isChild={isChild}
          isStarred={this.props.starredItemsByObjectId?.[item.id] ?? false}
        />
      );
    } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.TEST_DEFINITION) {
      return (
        <TestDefinitionSidepanelItem
          key={item.id}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          openResult={this.props.openResult as (item: TestDefinitionListingGraphQL) => void}
          testDefinition={item as TestDefinitionListingGraphQL}
          isChild={isChild}
        />
      );
    } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.TEST_ORDER) {
      return (
        <TestOrderSidepanelItem
          key={item.id}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          openResult={this.props.openResult as (item: TestOrderListingGraphQL) => void}
          testOrder={item as TestOrderListingGraphQL}
          isChild={isChild}
        />
      );
    } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.REQUEST_V2_SUBMISSION) {
      return (
        <RequestV2SubmissionSidepanelItem
          key={item.id}
          isActive={item.id === this.props.activeItemId}
          isCompact={this.props.isCompact}
          openResult={this.props.openResult}
          requestV2Submission={item as RequestV2SubmissionListingGraphQL}
          isChild={isChild}
        />
      );
    } else {
      return (
        <FolderFileSidepanelItem
          active={item.id === this.props.activeItemId}
          collection={this.props.collection}
          key={item.id}
          bioentityAliasInfos={this.props.bioentityAliasInfosByBioentityId[item.id]}
          finderId={this.props.finderId}
          isCompact={this.props.isCompact}
          isListing={!this.props.isSearch}
          openResult={this.props.openResult}
          result={item}
          isChild={isChild}
          isStarred={this.props.starredItemsByObjectId ? this.props.starredItemsByObjectId[item.id] : false}
        />
      );
    }
  }

  renderGroupSidepanelItem(group: ListableGroup): JSX.Element {
    return (
      <GroupSidepanelItem
        isActive={group.id === this.props.activeItemId}
        isCompact={this.props.isCompact}
        group={group}
        key={group.id}
        onClick={() => {
          this.onToggleExpanded(group);
        }}
        isExpanded={group.isExpanded}
      />
    );
  }

  renderGroups(): Array<JSX.Element> {
    const sidepanelItems: Array<JSX.Element> = [];
    for (const group of this.props.groups ?? []) {
      sidepanelItems.push(this.renderGroupSidepanelItem(group));
      if (group.isExpanded) {
        for (const item of group.items) {
          sidepanelItems.push(this.renderSidepanelItem(item, true));
        }

        if (group.areChildrenLoading) {
          sidepanelItems.push(<GroupChildrenLoadingIndicator key={`childLoader_${group.id}`} />);
        }

        const numChildrenLoaded = group.items.length;
        const numChildrenLoadable = Math.min(group.count - numChildrenLoaded, this.props.childPageSize);
        if (numChildrenLoaded > 0 && numChildrenLoadable > 0) {
          sidepanelItems.push(
            <GroupChildrenPager
              key={`pager_${group.id}`}
              numChildren={numChildrenLoadable}
              onClick={() => {
                this.props.onRequestMoreChildItems(group.id);
              }}
            />
          );
        }
      }
    }

    return sidepanelItems;
  }

  render(): JSX.Element {
    if (!this.props.items.length && !this.props.groups?.length) {
      return (
        <div
          className="sidepanel-fadedMessage"
          {...getTestProps({component: 'SidepanelItemList', element: 'emptyMessage'})}
        >
          {this.props.emptyMessage}
        </div>
      );
    }

    const listing = (
      <div {...getTestProps({component: 'SidepanelItemList', element: 'root'})}>
        {this.props.groups?.length
          ? this.renderGroups()
          : this.props.items.map((item) => this.renderSidepanelItem(item, false))}
      </div>
    );
    return this.props.folder ? (
      <FolderDropTarget
        shouldCaptureDrop={true}
        validateDrop={this._getFolderDropError}
        folder={this.props.folder}
        section={this.props.section}
      >
        {listing}
      </FolderDropTarget>
    ) : (
      listing
    );
  }
}

export const SIDEPANEL_ITEM_LIST_BIOENTITIES_QUERY = gql`
  query SidepanelItemListBioentitiesQuery($bioentityIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: SIGHTGLASS) {
    bioentities(ids: $bioentityIds) {
      ...AliasesForBioentityGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('AliasesForBioentity').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const SIDEPANEL_ITEM_LIST_FOLDER_QUERY = gql`
  query SidepanelItemListFolderQuery($folderId: StrictString!) @skipAudit @engComponent(name: SIGHTGLASS) {
    folder(id: $folderId) {
      ... on FolderNode {
        ...FolderWithPermissionsGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('FolderWithPermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function skipFolderQuery(folderId: string | null | undefined): boolean {
  return folderId == null;
}

export default function SidepanelItemListGraphQL(props: Props & WrappedComponentProps): JSX.Element | null {
  const {items, folderId, ...sidepanelProps} = props;

  const bioentityIds = items.flatMap((item) => (FileUtil.isInventoryFile(item) ? [item.id] : []));
  const bioentitiesResult = useBenchlingQuery<
    SidepanelItemListBioentitiesQuery,
    SidepanelItemListBioentitiesQueryVariables
  >(SIDEPANEL_ITEM_LIST_BIOENTITIES_QUERY, {
    variables: {bioentityIds},
  });

  const folderResult = useSkippableBenchlingQuery<
    SidepanelItemListFolderQuery,
    SidepanelItemListFolderQueryVariables
  >(SIDEPANEL_ITEM_LIST_FOLDER_QUERY, {
    skip: skipFolderQuery(folderId),
    variables: {folderId: folderId!},
  });

  const containersResult = useContainerValidContentsQuery(items);
  const storablesValidationResult = useStorableValidationInfoQuery(props.items);
  const starredItemsResult = useStarredItemsQuery(items);

  const hasLatestBeenFetched = props.asyncFetcherIsFetchedState[AsyncFetcherLocation.SIDEPANEL_ITEM_LIST];
  const asyncFetcherUpdate = props.onUpdateAsyncFetcherIsFetchedState;

  useEffect(() => {
    if (!hasLatestBeenFetched) {
      asyncFetcherUpdate(AsyncFetcherLocation.SIDEPANEL_ITEM_LIST, true);
    }
  }, [asyncFetcherUpdate, hasLatestBeenFetched]);

  if (!hasLatestBeenFetched) {
    // Need to refetch the async fetchers that kick off after the search endpoint returns
    bioentitiesResult.refetch();
    if (!skipFolderQuery(folderId)) {
      folderResult.refetch();
    }
    containersResult?.refetch();
    storablesValidationResult?.refetch();
    starredItemsResult.refetch();
  }

  if (
    bioentitiesResult.type === QueryResultType.PENDING ||
    folderResult.type === QueryResultType.PENDING ||
    starredItemsResult.type === QueryResultType.PENDING
  ) {
    return <CenteredHorizontalThrobber />;
  } else if (
    bioentitiesResult.type === QueryResultType.ERROR ||
    folderResult.type === QueryResultType.ERROR ||
    starredItemsResult.type === QueryResultType.ERROR
  ) {
    return null;
  } else {
    const folder =
      folderResult.type === QueryResultType.SKIPPED || isInaccessible(folderResult.data.folder)
        ? null
        : normalizeFolder(graphQLToREST('FolderWithPermissions', folderResult.data.folder));

    return (
      <SidepanelItemList
        {...sidepanelProps}
        bioentityAliasInfosByBioentityId={bioentityAliasInfosByBioentityId(
          bioentitiesResult.data.bioentities
        )}
        items={items}
        folder={folder}
        starredItemsByObjectId={starredItemsResult.starredItemsByObjectId}
        containerValidContentsInfoById={containersResult?.validContentsInfoByContainerId}
        storableValidationInfoById={storablesValidationResult?.storableValidationInfoById}
      />
    );
  }
}
